import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { GameStateResponse } from '@/lib/api';

interface GameStateEditorProps {
  state: GameStateResponse;
  onSave: (data: string, version: number) => Promise<void>;
  saving?: boolean;
  error?: string | null;
}

export function GameStateEditor({ state, onSave, saving, error }: GameStateEditorProps) {
  const [editData, setEditData] = useState(state.data ?? '');
  const [isDirty, setIsDirty] = useState(false);

  // Sync editor content when state changes (e.g., after save + refresh)
  useEffect(() => {
    setEditData(state.data ?? '');
    setIsDirty(false);
  }, [state.data, state.version]);

  const handleChange = (value: string) => {
    setEditData(value);
    setIsDirty(value !== (state.data ?? ''));
  };

  const handleSave = async () => {
    await onSave(editData, state.version);
    setIsDirty(false);
  };

  const isBinary = state.dataType === 'binary';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-mono text-lg">{state.keyword}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">v{state.version}</Badge>
            <Badge variant="secondary">{state.dataType}</Badge>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {state.dataSizeBytes.toLocaleString()} bytes &middot; Created{' '}
          {new Date(state.createdAt).toLocaleString()} &middot; Updated{' '}
          {new Date(state.updatedAt).toLocaleString()}
        </p>
        {state.checksum && (
          <p className="text-xs text-muted-foreground font-mono">SHA-256: {state.checksum}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isBinary ? (
          <div className="p-4 rounded-md bg-muted text-sm text-muted-foreground">
            Binary data cannot be edited in this view. Use the API directly to update binary state.
          </div>
        ) : (
          <>
            <Textarea
              value={editData}
              onChange={(e) => handleChange(e.target.value)}
              className="font-mono text-sm min-h-[200px]"
              placeholder="Enter state data..."
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={!isDirty || saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
